import React from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import DeclineModal from './DeclineModal';
import { Button, Icon } from 'semantic-ui-react';
import { TextLink } from '@teachstonellc/component-library';
import { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Subtitle,
  Actions,
  ButtonRow,
  ErrorWrapper
} from './Styled';

import {
  PARTICIPANT_STATUS,
  TrainingInvitation
} from 'types/api/TrainingParticipant';

import {
  getTrainingInvitation,
  respondTrainingInvitation
} from 'actions/trainings';

interface InvitationBannerProps {
  /** The training ID. */
  trainingId: number;

  /** Callback fired when the status is changed. */
  onChange: (status: PARTICIPANT_STATUS) => void;
}

const InvitationBanner: React.FC<InvitationBannerProps> = ({
  trainingId,
  onChange
}) => {
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [invitation, setInvitation] = useState<TrainingInvitation>();
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    getTrainingInvitation(trainingId).then(
      ({ data }) => {
        setInvitation(data);
        onChange(data.status);
      },
      () => {}
    );
  }, [trainingId, onChange]);

  function handleAccept() {
    setAccepting(true);
    setIsError(false);
    respondTrainingInvitation(trainingId, 'accept').then(
      ({ data }) => {
        setAccepting(false);
        setInvitation(data);
        onChange(data.status);
        window.location.reload();
      },
      () => {
        setAccepting(false);
        setIsError(true);
      }
    );
  }

  function handleDecline(data: TrainingInvitation) {
    setDeclining(false);
    setInvitation(data);
    onChange(data.status);
    window.location.reload();
  }

  function handleDeclineError() {
    setIsError(true);
    setDeclining(false);
  }

  if (!invitation || invitation.status !== 'invited') {
    return null;
  }

  return (
    <Container>
      <DeclineModal
        open={declining}
        trainingId={trainingId}
        afterDecline={handleDecline}
        onError={handleDeclineError}
        onClose={() => setDeclining(false)}
      />
      <div>
        <Title>{i18n.ft(messages.title)}</Title>
        <Subtitle>{i18n.ft(messages.subtitle)}</Subtitle>
      </div>
      <Actions>
        <ButtonRow>
          <Button
            size="small"
            color="green"
            loading={accepting}
            onClick={handleAccept}
            content={i18n.ft(messages.accept)}
            disabled={accepting || declining || !invitation.can_accept_training}
          />
          <Button
            size="small"
            onClick={() => {
              setIsError(false);
              setDeclining(true);
            }}
            content={i18n.ft(messages.decline)}
            disabled={
              accepting || declining || !invitation.can_decline_training
            }
          />
        </ButtonRow>
        {isError && (
          <ErrorWrapper role="alert" aria-live="polite">
            <Icon color="red" name="warning sign" aria-hidden="true" />
            <span className="ml-2">
              {i18n.ft(messages.error)}
              <TextLink
                href="https://teachstone.com/support/"
                target="_blank"
                className="[&>span]:font-[proxima-nova] [&>span]:text-ds-xs"
              >
                {i18n.ft(messages.errorLink)}
              </TextLink>
            </span>
          </ErrorWrapper>
        )}
      </Actions>
    </Container>
  );
};

export default InvitationBanner;
